/**
 * Every derived score in the app. Pure, so the client can compute optimistically during the rate
 * loop and the server can compute the same number on read — two implementations of one formula
 * drift, and the drift shows up as a score that flickers after save.
 *
 * Rulings implemented here are recorded in docs/DESIGN-CONFLICTS.md §J.
 */
export const SCORE_MIN = 1.0;
export const SCORE_MAX = 10.0;
/**
 * Album score. Defined only when every core track is rated — an incomplete album has no number
 * at all, not a provisional one. Bonus tracks never contribute.
 *
 * Two branches on duration and no third: either every core track has a duration and the mean is
 * duration-weighted, or one is missing and the whole thing falls back to unweighted and says so.
 * Mixing real durations with an invented median produces a number that is neither weighted nor
 * honest, and it is worse for being invisible.
 */
export function albumScore(tracks, cohesion = null) {
    const core = tracks.filter((t) => t.isCore);
    const rated = core.filter((t) => t.rating !== null);
    const complete = core.length > 0 && rated.length === core.length;
    const unweighted = rated.length > 0 ? rated.reduce((sum, t) => sum + t.rating, 0) / rated.length : null;
    const base = {
        unweighted,
        coreCount: core.length,
        ratedCoreCount: rated.length,
        complete,
    };
    const everyDuration = core.every((t) => t.durationMs !== null && t.durationMs > 0);
    const basis = everyDuration ? "duration-weighted" : "unweighted";
    if (!complete)
        return { ...base, score: null, preClamp: null, basis };
    let mean;
    if (everyDuration) {
        const total = core.reduce((sum, t) => sum + t.durationMs, 0);
        mean = core.reduce((sum, t) => sum + t.rating * t.durationMs, 0) / total;
    }
    else {
        mean = unweighted;
    }
    const raw = mean + (cohesion ?? 0);
    const score = clamp(raw, SCORE_MIN, SCORE_MAX);
    return {
        ...base,
        score,
        // Only reported when the clamp actually did something, so the tooltip stays quiet otherwise.
        preClamp: Math.abs(raw - score) > 1e-9 ? raw : null,
        basis,
    };
}
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
export const DEFAULT_SHRINKAGE = {
    m: 25,
    artistThreshold: 10,
    priorFixed: 7.0,
    priorMinRated: 200,
};
/** The gap at which RAW renders in the unrated gold, so a small sample visibly pays. */
export const RAW_PENALTY_GAP = 0.3;
/**
 * Artist score, shrunk toward a prior:  (n·raw + M·prior) / (n + M)
 *
 * The prior has a floor. Below `priorMinRated` total rated songs the collection mean is computed
 * from too little data to mean anything, so a fixed prior is used and the UI says so. Without
 * that floor the first artist to cross the threshold is shrunk toward a mean derived from a
 * handful of songs.
 */
export function artistScore(ratings, collection, settings = DEFAULT_SHRINKAGE) {
    const n = ratings.length;
    const raw = n > 0 ? ratings.reduce((a, b) => a + b, 0) / n : null;
    const useLive = collection.totalRated >= settings.priorMinRated && collection.mean !== null;
    const prior = useLive ? collection.mean : settings.priorFixed;
    const priorBasis = useLive ? "collection" : "fixed";
    if (n < settings.artistThreshold || raw === null) {
        return { score: null, raw, ratedCount: n, qualified: false, priorBasis, prior, penalised: false };
    }
    const score = (n * raw + settings.m * prior) / (n + settings.m);
    return {
        score,
        raw,
        ratedCount: n,
        qualified: true,
        priorBasis,
        prior,
        penalised: raw - score > RAW_PENALTY_GAP,
    };
}
/**
 * Competition ranking: ties share a rank and the next rank skips. With integer song scores across
 * thousands of songs, ties are the normal case, not an edge case — a rank-1 three-way tie is
 * followed by rank 4.
 *
 * `orderKeyOf` is the within-tier ordering key (see ordering.ts), and it is optional because
 * albums and artists score on floats and never need it. Where it is supplied, rows the user has
 * placed take real consecutive numerals at the head of their score group and the rest share the
 * numeral that follows — so a tie group the user has dragged into order becomes a real 1, 2, 3,
 * and **the next rank still skips**, exactly as it did when this was a client-side tie order.
 *
 * What it deliberately does NOT do is change where a score group starts. Ordering permutes rows
 * inside a tier; it never moves the tier. That is what keeps the MOVE column honest — see
 * Rankings.tsx, which feeds MOVE the ordering-blind rank.
 *
 * Unrated entities are pinned last with no rank at all.
 */
export function rank(items, scoreOf, orderKeyOf) {
    const keyOf = (item) => (orderKeyOf ? orderKeyOf(item) : null);
    const scored = items
        .filter((i) => scoreOf(i) !== null)
        // Array.prototype.sort is stable, so rows with no ordering key keep the order they arrived in
        // — which is the caller's, and for the rankings table that is the server's own ORDER BY.
        .sort((a, b) => {
        const byScore = scoreOf(b) - scoreOf(a);
        if (byScore !== 0)
            return byScore;
        const ka = keyOf(a);
        const kb = keyOf(b);
        if (ka === kb)
            return 0;
        // Unplaced sorts to the bottom of its own tier: unmeasured, never worst.
        if (ka === null)
            return 1;
        if (kb === null)
            return -1;
        return ka < kb ? -1 : 1;
    });
    const unscored = items.filter((i) => scoreOf(i) === null);
    const out = [];
    let index = 0;
    while (index < scored.length) {
        const score = scoreOf(scored[index]);
        let end = index;
        while (end < scored.length && scoreOf(scored[end]) === score)
            end += 1;
        // The placed rows sort first inside the group, so counting them is counting the prefix.
        let placed = index;
        while (placed < end && keyOf(scored[placed]) !== null)
            placed += 1;
        const placedCount = placed - index;
        for (let i = index; i < end; i += 1) {
            const isPlaced = i < placed;
            // Placed rows take their own numeral; everything else shares the one immediately after.
            const value = isPlaced ? i + 1 : index + placedCount + 1;
            out.push({
                item: scored[i],
                rank: value,
                repeat: out.length > 0 && out[out.length - 1].rank === value,
                unplaced: !isPlaced && placedCount > 0,
            });
        }
        index = end;
    }
    for (const item of unscored)
        out.push({ item, rank: null, repeat: false, unplaced: false });
    return out;
}
/** Coverage for the 84×4px bar: flash under 80%, ink at or above. */
export function coverage(rated, owned) {
    const ratio = owned > 0 ? rated / owned : 0;
    return { ratio, low: ratio < 0.8 };
}
//# sourceMappingURL=scoring.js.map